/**
 * Who is allowed to sign in, before the `UserAccounts` allowlist gets a say.
 *
 * Two providers are live while we migrate off Google Workspace:
 *
 * - `microsoft-entra-id` — the source of truth for collana.com identities.
 * - `google` — the legacy Google Workspace login, kept for the transition.
 *
 * Both only establish *which collana.com person* is at the keyboard. Whether that person may use
 * the dashboard at all is still decided by the `UserAccounts` lookup in the `jwt` callback in
 * `auth.ts`, so every rule here is the outer of two gates.
 */

/** Where the sign-in page lives. Referenced by `auth.ts`, the middleware and the app shell. */
export const signInPagePath = '/signin';

/** Every identity allowed to sign in has an email address on this domain. */
export const allowedEmailDomain = 'collana.com';

/**
 * Google's Hosted Domain claim, which is only present for Google Workspace *members* of the domain.
 * That makes it a stronger signal than the email address alone: anyone can create a consumer Google
 * account on an address they merely receive mail at, and such an account carries no `hd` at all.
 */
export const allowedGoogleHostedDomain = allowedEmailDomain;

/** Rejections carry a reason so the callback can log something specific enough to debug. */
export type SignInDecision = { allowed: true; email: string } | { allowed: false; reason: string };

// `null` is allowed on every claim because that is how Auth.js types the profile it hands the
// `signIn` callback.
interface GoogleIdTokenClaims {
  hd?: string | null;
  email?: string | null;
}

interface EntraIdTokenClaims {
  email?: string | null;
  preferred_username?: string | null;
  upn?: string | null;
}

/**
 * Entra does not guarantee an `email` claim: it is there by default for guests, but for managed
 * users it depends on the app registration's optional claims and on the account having a mail
 * attribute. `preferred_username` is always present in a v2.0 id token but is not necessarily an
 * email address, so candidates that don't look like one are skipped.
 *
 * Emails are always stored lowercase (see the `AddLowercaseEmails` migration), which is what lets
 * the `UserAccounts` lookup compare them with a plain `=`.
 */
export const resolveEntraEmail = (claims: EntraIdTokenClaims) =>
  [claims.email, claims.preferred_username, claims.upn]
    .find((candidate) => candidate?.includes('@'))
    ?.toLowerCase();

export const decideGoogleSignIn = (claims: GoogleIdTokenClaims): SignInDecision => {
  const email = claims.email?.toLowerCase();

  if (claims.hd !== allowedGoogleHostedDomain) {
    return {
      allowed: false,
      reason: `${email ?? 'an account with no email'} is not a ${allowedGoogleHostedDomain} Google Workspace member (hd=${claims.hd ?? 'absent'})`,
    };
  }

  if (!email) {
    return { allowed: false, reason: 'Google returned no email address' };
  }

  return { allowed: true, email };
};

export const decideEntraSignIn = (claims: EntraIdTokenClaims): SignInDecision => {
  const email = resolveEntraEmail(claims);

  if (!email) {
    return {
      allowed: false,
      reason: 'none of the email, preferred_username or upn claims held an email address',
    };
  }

  // Which tenant the id token can even come from is enforced by the Entra app registration itself
  // (restricted to collana tenant accounts), not here — this is the address check on top of that.
  if (!email.endsWith(`@${allowedEmailDomain}`)) {
    return {
      allowed: false,
      reason: `${email} is not a ${allowedEmailDomain} account`,
    };
  }

  return { allowed: true, email };
};

/** Applies the rule belonging to the provider that authenticated. */
export const decideSignIn = (
  provider: string | undefined,
  claims: GoogleIdTokenClaims & EntraIdTokenClaims
): SignInDecision => {
  switch (provider) {
    case 'microsoft-entra-id':
      return decideEntraSignIn(claims);
    case 'google':
      return decideGoogleSignIn(claims);
    default:
      return { allowed: false, reason: `unrecognised provider ${provider ?? 'none'}` };
  }
};
